/**
 * CookieBanner — LGPD/GDPR-friendly consent layer.
 *
 * Shows a bottom-sheet on first visit. User can:
 *   • Aceitar tudo  → essentials + analytics + marketing
 *   • Só essenciais → essentials only (no analytics, no remarketing)
 *   • Customizar    → granular toggles (essentials always ON)
 *
 * Persists choice in `localStorage["alp.cookie_consent"]` so we don't pester
 * returning visitors. The shape stored:
 *   { essentials: true, analytics: bool, marketing: bool, ts: ISO }
 *
 * The banner is mounted ONCE near the app root. Pages can consume the
 * current preference via `getConsent()` (default-deny for analytics +
 * marketing until the user explicitly opts in).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, X, Settings2 } from "lucide-react";

const STORAGE_KEY = "alp.cookie_consent";

const DEFAULT_DENY = {
  essentials: true,
  analytics: false,
  marketing: false,
};

export function getConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) { return null; }
}

function persist(prefs) {
  try {
    window.localStorage.setItem(STORAGE_KEY,
      JSON.stringify({ ...prefs, ts: new Date().toISOString() }));
    // Fire a window event so other components (analytics loader, etc.)
    // can react without a full reload.
    window.dispatchEvent(new CustomEvent("alp:consent", { detail: prefs }));
  } catch (_e) { /* localStorage disabled — best-effort */ }
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_DENY);

  useEffect(() => {
    if (!getConsent()) setOpen(true);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    const all = { essentials: true, analytics: true, marketing: true };
    persist(all);
    setOpen(false);
  };
  const acceptEssentialsOnly = () => {
    persist(DEFAULT_DENY);
    setOpen(false);
  };
  const saveCustom = () => {
    persist(prefs);
    setOpen(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Consentimento de cookies"
      className="fixed bottom-0 left-0 right-0 z-[9990] bg-[#0F172A] text-white border-t-2 border-[#C9A24E] shadow-2xl"
      style={{ background: "linear-gradient(180deg,#0F172A 0%,#1E293B 100%)" }}
    >
      <div className="max-w-[1100px] mx-auto px-4 md:px-5 py-3 md:py-4"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        {!showCustom ? (
          <div className="flex flex-col md:flex-row gap-2.5 md:gap-4 items-start md:items-center">
            <div className="flex items-center gap-2 md:contents">
              <Cookie className="w-5 h-5 md:w-6 md:h-6 text-[#C9A24E] shrink-0" />
              <strong className="text-[13px] md:hidden">Cookies &amp; Privacidade</strong>
            </div>
            <div className="flex-1 text-[12px] md:text-[13px] leading-snug md:leading-relaxed">
              <strong className="hidden md:block mb-1">Cookies &amp; Privacidade</strong>
              <span className="hidden sm:inline">
                Usamos cookies essenciais para o funcionamento da plataforma, e
                opcionais para analytics anônimo e marketing personalizado.
                Você decide o que aceita.{" "}
              </span>
              <span className="sm:hidden">Usamos cookies essenciais e opcionais. </span>
              <Link to="/politica-de-privacidade" className="text-[#C9A24E] underline">
                Política de Privacidade
              </Link>
              {" · "}
              <Link to="/lgpd" className="text-[#C9A24E] underline">
                LGPD
              </Link>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0 w-full md:w-auto">
              <button
                data-testid="cookie-customize"
                onClick={() => setShowCustom(true)}
                className="px-3 py-2 text-[12px] font-bold rounded-lg border border-white/30 hover:bg-white/10 inline-flex items-center gap-1"
              >
                <Settings2 className="w-3 h-3" /> Customizar
              </button>
              <button
                data-testid="cookie-essentials-only"
                onClick={acceptEssentialsOnly}
                className="px-3 py-2 text-[12px] font-bold rounded-lg border border-white/30 hover:bg-white/10"
              >
                Só essenciais
              </button>
              <button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="px-4 py-2 min-h-[44px] md:min-h-0 flex-1 md:flex-none justify-center text-[12px] font-bold rounded-lg bg-[#C9A24E] hover:bg-[#dcb670] text-[#0A1233] inline-flex items-center gap-1"
              >
                <ShieldCheck className="w-3 h-3" /> Aceitar tudo
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#C9A24E]" />
              <strong className="text-[14px]">Preferências de cookies</strong>
              <button onClick={() => setShowCustom(false)}
                aria-label="Voltar"
                className="ml-auto text-white/60 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Toggle
                testId="cookie-toggle-essentials"
                title="Essenciais"
                desc="Sessão, segurança, autenticação. Sempre ativos — não bloqueáveis."
                checked={true}
                disabled
                onChange={() => {}}
              />
              <Toggle
                testId="cookie-toggle-analytics"
                title="Analytics"
                desc="Posthog anonimizado para entender uso da plataforma."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <Toggle
                testId="cookie-toggle-marketing"
                title="Marketing"
                desc="Remarketing e pixels (Google Ads, Meta) — só com seu OK."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                data-testid="cookie-save-custom"
                onClick={saveCustom}
                className="px-4 py-2 text-[12px] font-bold rounded-lg bg-[#C9A24E] hover:bg-[#dcb670] text-[#0A1233]"
              >
                Salvar preferências
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ title, desc, checked, disabled, onChange, testId }) {
  return (
    <label
      data-testid={testId}
      className={`flex items-start gap-2 p-3 rounded-lg border ${
        disabled ? "opacity-70" : "cursor-pointer hover:bg-white/5"
      } border-white/15`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-[#C9A24E]"
      />
      <div>
        <div className="text-[12px] font-bold">{title}</div>
        <div className="text-[11px] text-white/55 leading-snug">{desc}</div>
      </div>
    </label>
  );
}
